import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Coins,
  Flame,
  Gift,
  Loader2,
  LogIn,
  Repeat2,
  Trophy,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import SafeImage from '../../components/SafeImage';
import { claimEventCupMilestone, exchangeEventCups, fetchEventCupWallet } from '../../services/eventCups';
import { fetchFifaKitConfig, resolveFifaKitImageUrl } from '../../services/fifaKit';

const FIFA_KIT_PAGE_IMAGE = '';

const EMPTY_CUP_WALLET = {
  balance_cups: 0,
  lifetime_earned_cups: 0,
  lifetime_spent_cups: 0,
  limits: {
    fp_cups_remaining_today: 500,
    wallet_cups_remaining_this_week: 1000,
  },
  rules: {
    exchange: {
      fp: { cups: 100, amount: 5, label: '100 Cups = 5 FP' },
      wallet: { cups: 1000, amount: 10, label: '1000 Cups = $10 wallet' },
      fp_daily_cup_cap: 500,
      wallet_weekly_cup_cap: 1000,
    },
    earning: {
      login_rewards: [2, 3, 4, 5, 6, 8, 12],
      prediction_submit_cups: 1,
      prediction_submit_daily_cap: 5,
      suggested_exact_score_cups: 25,
      prediction_streak_bonuses: { 2: 25, 3: 50, 5: 100 },
      worldcup_weekly_max_cups: 120,
      referral_verified_cups: 50,
      referral_first_deposit_cups: 100,
    },
  },
  history: [],
  alerts: [],
  claims: {
    login_rewards: {
      current_streak: 0,
      today_claimed: false,
      today_cups: 0,
      rewards: [2, 3, 4, 5, 6, 8, 12],
    },
    prediction_streaks: {
      current_streak: 0,
      submitted_this_week: 0,
      wins_this_week: 0,
      rows: [],
    },
    worldcup_tasks: {
      period_key: '',
      rows: [],
      claimable_count: 0,
      claimed_count: 0,
    },
    referral_streak: {
      period_key: 'lifetime',
      rows: [],
      claimable_count: 0,
      claimed_count: 0,
    },
    claimable_count: 0,
  },
};

const rewardCards = [
  {
    key: 'prediction-streaks',
    title: 'Prediction Streaks',
    sub: 'Correct scores earn cups',
    icon: Flame,
    accent: '#F97316',
    gradient: 'linear-gradient(180deg, #FB923C 0%, #DC2626 100%)',
  },
  {
    key: 'login-rewards',
    title: 'Login Rewards',
    sub: '2-12 cups daily streak',
    icon: LogIn,
    accent: '#22D3EE',
    gradient: 'linear-gradient(180deg, #38BDF8 0%, #2563EB 100%)',
  },
  {
    key: 'worldcup-task',
    title: 'Worldcup Task',
    sub: 'Weekly cup goals, capped safe',
    icon: CalendarDays,
    accent: '#A78BFA',
    gradient: 'linear-gradient(180deg, #A78BFA 0%, #7C3AED 100%)',
  },
  {
    key: 'referral-streak',
    title: 'Referral Streak',
    sub: 'Verified referrals earn cups',
    icon: Users,
    accent: '#FACC15',
    gradient: 'linear-gradient(180deg, #FACC15 0%, #F59E0B 100%)',
  },
  {
    key: 'exchange-center',
    title: 'Exchange Center',
    sub: '100 Cups = 5 FP, 1000 = $10',
    icon: Repeat2,
    accent: '#60A5FA',
    gradient: 'linear-gradient(180deg, #60A5FA 0%, #4F46E5 100%)',
    lines: ['Exchange Cups into FP or wallet balance.', 'Wallet exchange is capped at $10 per week.'],
  },
];

const formatCups = (value) => Math.floor(Number(value || 0)).toLocaleString();

export default function FifaKitPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [fifaKitConfig, setFifaKitConfig] = useState(null);
  const [cupWallet, setCupWallet] = useState(EMPTY_CUP_WALLET);
  const [cupLoading, setCupLoading] = useState(true);
  const [activePanel, setActivePanel] = useState(null);
  const [exchanging, setExchanging] = useState('');
  const [claimingKey, setClaimingKey] = useState('');
  const [promoNoteVisible, setPromoNoteVisible] = useState(false);
  const promoNoteTimerRef = useRef(null);

  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const predictScorePath = tenantSlug ? `/${tenantSlug}/app/explore/predict-score` : '/app/explore/predict-score';
  const pageImage = resolveFifaKitImageUrl(fifaKitConfig?.page_image_url, FIFA_KIT_PAGE_IMAGE);
  const selectedCard = rewardCards.find((item) => item.key === activePanel);
  const cupBalance = Number(cupWallet?.balance_cups || 0);
  const exchangeRules = cupWallet?.rules?.exchange || EMPTY_CUP_WALLET.rules.exchange;
  const limits = cupWallet?.limits || EMPTY_CUP_WALLET.limits;

  const showCupAlerts = (data) => {
    const alerts = data?.alerts || [];
    alerts.forEach((alert) => {
      const key = `event-cup-alert:${alert.key || alert.title}`;
      if (!alert?.title || sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
      toast.message(alert.title, {
        description: alert.body || '',
        duration: 6500,
        action: alert.panel
          ? {
              label: alert.type === 'claim_available' ? 'Claim now' : 'View',
              onClick: () => setActivePanel(alert.panel),
            }
          : undefined,
      });
    });
  };

  const loadCupWallet = async () => {
    setCupLoading(true);
    try {
      const data = await fetchEventCupWallet();
      setCupWallet({ ...EMPTY_CUP_WALLET, ...data });
      showCupAlerts(data);
    } catch (error) {
      setCupWallet(EMPTY_CUP_WALLET);
    } finally {
      setCupLoading(false);
    }
  };

  useEffect(() => {
    let stopped = false;
    fetchFifaKitConfig()
      .then((item) => {
        if (!stopped) setFifaKitConfig(item);
      })
      .catch(() => {
        if (!stopped) setFifaKitConfig(null);
      });
    return () => {
      stopped = true;
    };
  }, [tenantSlug]);

  useEffect(() => {
    loadCupWallet();
  }, [tenantSlug]);

  useEffect(() => () => {
    if (promoNoteTimerRef.current) clearTimeout(promoNoteTimerRef.current);
  }, []);

  const handleCardClick = (item) => {
    setActivePanel(item.key);
  };

  const showPromoNote = () => {
    setPromoNoteVisible(true);
    if (promoNoteTimerRef.current) clearTimeout(promoNoteTimerRef.current);
    promoNoteTimerRef.current = setTimeout(() => {
      setPromoNoteVisible(false);
    }, 4000);
  };

  const handleClaim = async (key) => {
    if (!key) return;
    setClaimingKey(key);
    try {
      const data = await claimEventCupMilestone(key);
      if (data.event_cups) {
        setCupWallet({ ...EMPTY_CUP_WALLET, ...data.event_cups });
      } else {
        await loadCupWallet();
      }
      toast.success(`${formatCups(data.cups)} Cups claimed`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Cup claim is not ready yet');
    } finally {
      setClaimingKey('');
    }
  };

  const handleExchange = async (target) => {
    setExchanging(target);
    try {
      const data = await exchangeEventCups(target);
      if (data.event_cups) {
        setCupWallet({ ...EMPTY_CUP_WALLET, ...data.event_cups });
      } else {
        await loadCupWallet();
      }
      toast.success(target === 'wallet' ? '$10 added to wallet' : '5 FP added to your wallet');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not exchange cups');
    } finally {
      setExchanging('');
    }
  };

  return (
    <div className="player-app min-h-screen pb-8" data-testid="fifa-kit-page">
      <header
        className="px-4 sm:px-6 pt-3 pb-2 flex items-center justify-between"
        style={{ background: 'var(--vl-bg, #000000)' }}
      >
        <button
          onClick={() => navigate(homePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="fifa-kit-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={() => setActivePanel('exchange-center')}
          className="h-10 rounded-full px-3 flex items-center gap-2 transition active:scale-95"
          style={{
            background: 'linear-gradient(135deg, rgba(255,217,61,0.16), rgba(96,165,250,0.12))',
            border: '1px solid rgba(255,217,61,0.36)',
            boxShadow: '0 10px 24px rgba(255,217,61,0.10), inset 0 1px 0 rgba(255,255,255,0.10)',
            color: '#FFFFFF',
          }}
          data-testid="event-cup-wallet"
          aria-label="Event cup wallet"
        >
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(180deg, #FFD93D 0%, #F59E0B 100%)',
              color: '#3F2A00',
            }}
            aria-hidden
          >
            <Trophy className="w-4 h-4" strokeWidth={2.7} />
          </span>
          <span className="display text-sm font-extrabold leading-none">
            {cupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : formatCups(cupBalance)}
          </span>
          <span className="text-[10px] font-black uppercase tracking-[0.12em] leading-none" style={{ color: 'rgba(255,255,255,0.72)' }}>
            Cups
          </span>
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-2 pb-5 space-y-3">
        <section
          className="overflow-hidden rounded-[28px]"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.065), rgba(245,215,110,0.075) 50%, rgba(34,211,238,0.055))',
            border: '1px solid rgba(245,215,110,0.28)',
            boxShadow: '0 18px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <div
            className="relative aspect-[1600/760] min-h-[210px] w-full"
            style={{
              background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.045) 0 10px, rgba(255,255,255,0.025) 10px 20px)',
            }}
            data-testid="fifa-kit-image-slot"
          >
            <SafeImage
              src={pageImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              fallback={null}
              draggable={false}
            />
          </div>
        </section>

        <button
          onClick={() => navigate(predictScorePath)}
          className="w-full flex items-center gap-3 sm:gap-4 rounded-[22px] px-3 sm:px-4 py-3 transition active:scale-[0.99] hover:brightness-110 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(124,58,237,0.16) 54%, rgba(255,217,61,0.12) 100%)',
            border: '1px solid rgba(96,165,250,0.42)',
            boxShadow: '0 12px 28px rgba(37,99,235,0.16), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
          data-testid="score-predictions-cta"
          aria-label="Predict the Score"
        >
          <span
            className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(180deg, #FFD93D 0%, #2563EB 100%)',
              boxShadow: '0 10px 22px rgba(37,99,235,0.34), inset 0 1px 0 rgba(255,255,255,0.35)',
            }}
            aria-hidden
          >
            <Trophy className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={2.6} />
          </span>

          <div className="flex-1 text-left min-w-0">
            <p
              className="display font-extrabold text-base sm:text-lg leading-tight"
              style={{ color: '#FFFFFF' }}
            >
              Predict the Score
            </p>
            <p
              className="text-xs sm:text-sm leading-tight mt-0.5 truncate"
              style={{ color: 'rgba(255,255,255,0.72)' }}
            >
              Exact score wins Cups
            </p>
          </div>

          <ChevronRight
            className="w-5 h-5 shrink-0"
            style={{ color: '#FFD93D' }}
            aria-hidden="true"
          />
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {rewardCards.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleCardClick(item)}
                className="w-full flex items-center gap-3 rounded-[22px] px-3 sm:px-4 py-3 transition active:scale-[0.99] hover:brightness-110 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.055) 0%, rgba(96,165,250,0.06) 54%, rgba(255,217,61,0.045) 100%)',
                  border: `1px solid ${item.accent}66`,
                  boxShadow: `0 10px 24px ${item.accent}1F, inset 0 1px 0 rgba(255,255,255,0.07)`,
                }}
                data-testid={`event-reward-${item.key}`}
                aria-label={item.title}
              >
                <span
                  className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    background: item.gradient,
                    boxShadow: `0 10px 22px ${item.accent}55, inset 0 1px 0 rgba(255,255,255,0.35)`,
                  }}
                  aria-hidden
                >
                  <Icon className="w-6 h-6" style={{ color: item.key === 'referral-streak' ? '#3F2A00' : '#FFFFFF' }} strokeWidth={2.5} />
                </span>

                <div className="flex-1 text-left min-w-0">
                  <p
                    className="display font-extrabold text-base sm:text-lg leading-tight"
                    style={{ color: '#FFFFFF' }}
                  >
                    {item.title}
                  </p>
                  <p
                    className="text-xs sm:text-sm leading-tight mt-0.5 truncate"
                    style={{ color: 'rgba(255,255,255,0.70)' }}
                  >
                    {item.sub}
                  </p>
                </div>

                <ChevronRight
                  className="w-5 h-5 shrink-0"
                  style={{ color: item.accent }}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setActivePanel('exchange-center')}
          className="w-full flex items-center justify-center gap-2 rounded-[18px] px-4 py-3 transition active:scale-[0.99] hover:brightness-110"
          style={{
            background: 'rgba(255,217,61,0.09)',
            border: '1px solid rgba(255,217,61,0.28)',
            color: '#FDE68A',
          }}
          data-testid="event-cup-exchange-note"
        >
          <Coins className="w-4 h-4" strokeWidth={2.5} />
          <span className="text-sm font-bold">Exchange Cups: 100 = 5 FP, 1000 = $10 wallet</span>
        </button>
      </main>

      {activePanel && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/58 px-3 pb-3 backdrop-blur-sm sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-[28px] border"
            style={{
              background: 'linear-gradient(160deg, rgba(14,24,38,0.98), rgba(25,51,73,0.98))',
              borderColor: 'rgba(255,255,255,0.16)',
              boxShadow: '0 24px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
                  style={{ background: selectedCard?.gradient || 'linear-gradient(180deg, #FFD93D, #F59E0B)' }}
                >
                  {activePanel === 'exchange-center' ? (
                    <Repeat2 className="h-5 w-5 text-white" strokeWidth={2.6} />
                  ) : (
                    selectedCard && <selectedCard.icon className="h-5 w-5 text-white" strokeWidth={2.6} />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="display truncate text-lg font-black text-white">
                    {activePanel === 'exchange-center' ? 'Exchange Center' : selectedCard?.title}
                  </p>
                  <p className="text-xs text-white/55">
                    Balance: {formatCups(cupBalance)} Cups
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActivePanel(null)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 active:scale-95"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {activePanel === 'exchange-center' ? (
              <ExchangePanel
                cupBalance={cupBalance}
                exchangeRules={exchangeRules}
                limits={limits}
                exchanging={exchanging}
                onExchange={handleExchange}
              />
            ) : (
              <InfoPanel
                item={selectedCard}
                cupWallet={cupWallet}
                claimingKey={claimingKey}
                promoNoteVisible={promoNoteVisible}
                onClaim={handleClaim}
                onShowPromoNote={showPromoNote}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoPanel({ item, cupWallet, claimingKey, promoNoteVisible, onClaim, onShowPromoNote }) {
  if (!item) return null;
  const claims = cupWallet?.claims || EMPTY_CUP_WALLET.claims;
  const earningRules = cupWallet?.rules?.earning || EMPTY_CUP_WALLET.rules.earning;
  const loginRewards = earningRules?.login_rewards || EMPTY_CUP_WALLET.rules.earning.login_rewards;
  const login = claims?.login_rewards || EMPTY_CUP_WALLET.claims.login_rewards;
  const prediction = claims?.prediction_streaks || EMPTY_CUP_WALLET.claims.prediction_streaks;
  const worldcup = claims?.worldcup_tasks || EMPTY_CUP_WALLET.claims.worldcup_tasks;
  const referral = claims?.referral_streak || EMPTY_CUP_WALLET.claims.referral_streak;

  return (
    <div className="space-y-4 px-4 py-4">
      {item.key === 'login-rewards' && (
        <LoginRewardJourney login={login} loginRewards={loginRewards} />
      )}

      {item.key === 'prediction-streaks' && (
        <JourneySection
          rows={prediction.rows || []}
          emptyText="Predict exact scores to build your streak."
          claimingKey={claimingKey}
          onClaim={onClaim}
        />
      )}

      {item.key === 'worldcup-task' && (
        <JourneySection
          rows={worldcup.rows || []}
          emptyText="Tasks update as you predict, log in, and win exact scores."
          claimingKey={claimingKey}
          onClaim={onClaim}
        />
      )}

      {item.key === 'referral-streak' && (
        <JourneySection
          rows={referral.rows || []}
          emptyText="Invite real players to unlock referral cup claims."
          claimingKey={claimingKey}
          onClaim={onClaim}
        />
      )}

      <CupValueNote visible={promoNoteVisible} onShow={onShowPromoNote} />
    </div>
  );
}

function LoginRewardJourney({ login, loginRewards }) {
  const currentStreak = Number(login?.current_streak || 0);
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100/70">Today</p>
            <p className="display mt-1 text-base font-black text-white">
              {login?.today_claimed ? `${formatCups(login.today_cups)} Cups claimed` : 'Open daily to keep streak'}
            </p>
          </div>
          <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-black text-cyan-100">
            Day {Math.max(1, Math.min(currentStreak || 1, loginRewards.length))}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {loginRewards.map((cups, index) => {
          const day = index + 1;
          const active = currentStreak === day;
          const reached = currentStreak >= day;
          return (
            <div
              key={`${index}-${cups}`}
              className="rounded-xl border px-1 py-2 text-center"
              style={{
                background: active ? 'rgba(34,211,238,0.22)' : reached ? 'rgba(255,217,61,0.12)' : 'rgba(34,211,238,0.08)',
                borderColor: active ? 'rgba(34,211,238,0.65)' : reached ? 'rgba(255,217,61,0.34)' : 'rgba(34,211,238,0.20)',
              }}
            >
              <p className="text-[9px] font-black uppercase tracking-[0.08em] text-cyan-100/70">Day {day}</p>
              <p className="display mt-1 flex items-center justify-center gap-1 text-sm font-black text-white">
                <Trophy className="h-3.5 w-3.5 text-[#FFD93D]" strokeWidth={2.8} />
                {cups}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JourneySection({ rows, emptyText, claimingKey, onClaim }) {
  if (!rows?.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center text-sm font-semibold text-white/62">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      {rows.map((row) => (
        <JourneyRow
          key={`${row.key}-${row.period_key || 'now'}`}
          row={row}
          busy={claimingKey === row.key}
          onClaim={onClaim}
        />
      ))}
    </div>
  );
}

function JourneyRow({ row, busy, onClaim }) {
  const current = Number(row.current || 0);
  const target = Math.max(1, Number(row.target || 1));
  const pct = Math.min(100, Math.max(0, (current / target) * 100));
  const claimed = Boolean(row.claimed);
  const canClaim = Boolean(row.can_claim);
  const autoCredit = Boolean(row.auto_credit);
  const reward = Number(row.reward_cups || 0);
  const buttonLabel = claimed ? 'Claimed' : autoCredit ? (current >= target ? 'Credited' : 'Auto') : canClaim ? 'Claim' : `${current}/${target}`;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-black text-white">{row.title}</p>
            <span className="flex shrink-0 items-center gap-1 rounded-full border border-[#FFD93D]/20 bg-[#FFD93D]/10 px-2 py-1 text-[11px] font-black text-[#FDE68A]">
              <Trophy className="h-3.5 w-3.5" strokeWidth={2.7} />
              {reward > 0 ? formatCups(reward) : 'Match'}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full"
              style={{
                width: `${pct}%`,
                background: canClaim || claimed ? 'linear-gradient(90deg, #22C55E, #FFD93D)' : 'linear-gradient(90deg, #38BDF8, #6366F1)',
              }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => onClaim(row.key)}
          disabled={!canClaim || busy}
          className="flex h-10 min-w-[76px] shrink-0 items-center justify-center rounded-xl px-3 text-xs font-black transition active:scale-95 disabled:opacity-70"
          style={{
            background: canClaim ? 'linear-gradient(180deg, #FFD93D, #F59E0B)' : 'rgba(255,255,255,0.08)',
            color: canClaim ? '#241600' : 'rgba(255,255,255,0.72)',
            border: `1px solid ${canClaim ? 'rgba(255,217,61,0.65)' : 'rgba(255,255,255,0.10)'}`,
          }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
        </button>
      </div>
    </div>
  );
}

function CupValueNote({ visible, onShow }) {
  return (
    <div className="rounded-2xl border border-[#FFD93D]/20 bg-[#FFD93D]/10 p-3">
      <button
        type="button"
        onClick={onShow}
        className="flex w-full items-center gap-3 text-left active:scale-[0.99]"
      >
        <Gift className="h-5 w-5 shrink-0 text-[#FFD93D]" />
        <span className="text-sm font-black text-[#FDE68A]">
          {visible ? 'Cup value note' : 'Tap to view cup value note'}
        </span>
      </button>
      {visible && (
        <p className="mt-2 pl-8 text-sm font-bold leading-relaxed text-[#FDE68A]">
          Cups are promo points first. Real wallet value is only created when the player exchanges inside the capped Exchange Center.
        </p>
      )}
    </div>
  );
}

function ExchangePanel({ cupBalance, exchangeRules, limits, exchanging, onExchange }) {
  const fpRule = exchangeRules?.fp || EMPTY_CUP_WALLET.rules.exchange.fp;
  const walletRule = exchangeRules?.wallet || EMPTY_CUP_WALLET.rules.exchange.wallet;
  const fpRemaining = Number(limits?.fp_cups_remaining_today ?? EMPTY_CUP_WALLET.limits.fp_cups_remaining_today);
  const walletRemaining = Number(limits?.wallet_cups_remaining_this_week ?? EMPTY_CUP_WALLET.limits.wallet_cups_remaining_this_week);
  const canFp = cupBalance >= Number(fpRule.cups || 0) && fpRemaining >= Number(fpRule.cups || 0);
  const canWallet = cupBalance >= Number(walletRule.cups || 0) && walletRemaining >= Number(walletRule.cups || 0);

  return (
    <div className="space-y-4 px-4 py-4">
      <div className="grid grid-cols-3 gap-2">
        <Metric label="Cups" value={formatCups(cupBalance)} />
        <Metric label="FP cap left" value={formatCups(fpRemaining)} />
        <Metric label="Wallet cap left" value={formatCups(walletRemaining)} />
      </div>

      <ExchangeOption
        icon={Coins}
        title="Exchange for FP"
        sub={`${formatCups(fpRule.cups)} Cups gives ${fpRule.amount} FP`}
        disabled={!canFp || Boolean(exchanging)}
        busy={exchanging === 'fp'}
        accent="#22D3EE"
        onClick={() => onExchange('fp')}
      />

      <ExchangeOption
        icon={Wallet}
        title="Exchange for Wallet"
        sub={`${formatCups(walletRule.cups)} Cups gives $${Number(walletRule.amount || 0).toFixed(2)} wallet`}
        disabled={!canWallet || Boolean(exchanging)}
        busy={exchanging === 'wallet'}
        accent="#34D399"
        onClick={() => onExchange('wallet')}
      />

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
        <p className="text-xs font-bold leading-relaxed text-white/62">
          Wallet exchange is limited to $10 per player per week. FP exchange is limited to 500 Cups per day. These caps keep rewards useful for players without opening unlimited agent liability.
        </p>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-2 py-3 text-center">
      <p className="text-[9px] font-black uppercase tracking-[0.11em] text-white/45">{label}</p>
      <p className="display mt-1 text-sm font-black text-white">{value}</p>
    </div>
  );
}

function ExchangeOption({ icon: Icon, title, sub, disabled, busy, accent, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition active:scale-[0.99] disabled:opacity-50"
      style={{
        background: `linear-gradient(135deg, ${accent}22, rgba(255,255,255,0.045))`,
        borderColor: `${accent}66`,
      }}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${accent}33`, color: accent }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" strokeWidth={2.6} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="display block text-base font-black text-white">{title}</span>
        <span className="block text-xs font-semibold text-white/62">{sub}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0" style={{ color: accent }} />
    </button>
  );
}
